import { Close } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { ReactNode } from "react";

import { hasMouseSupport } from "utils/misc";

export function CanvasHudLineLabel({
    id,
    left,
    top,
    bgColor,
    textColor,
    onRemove,
    children,
    onPointerEnter,
    onPointerLeave,
    fontFamily = "Inter, sans-serif",
    fontSize = "12px",
    fontWeight = 600,
    lineHeight = "16px",
}: {
    id: string;
    left?: string;
    top?: string;
    bgColor: string;
    textColor: string;
    onRemove?: () => void;
    children: ReactNode;
    onPointerEnter?: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerLeave?: (e: React.PointerEvent<HTMLDivElement>) => void;
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: number;
    lineHeight?: string;
}) {
    const interactive = Boolean(onRemove);
    const hasMouse = hasMouseSupport();
    // Without hover there is nothing to reveal the remove button, so on touch it is always shown,
    // and at the larger size the coordinate labels use for touch.
    const touchRemove = onRemove && !hasMouse;

    return (
        <Box
            id={id}
            style={{
                left,
                top,
            }}
            className={hasMouse ? undefined : "line-label-hovered"}
            sx={{
                position: "absolute",
                translate: "-50% -50%",
                fontFamily,
                fontSize,
                fontStyle: "normal",
                fontWeight,
                lineHeight,
                letterSpacing: "0.25px",
                ...(touchRemove && {
                    display: "flex",
                    alignItems: "center",
                }),
                ...(interactive
                    ? {
                          "&.line-label-hovered": {
                              zIndex: 1051,
                          },
                          "&:not(.line-label-hovered) .laser-label-btn-container": {
                              width: 0,
                              paddingLeft: 0,
                              paddingRight: 0,
                          },
                          "&.line-label-hovered .label-label-text": {
                              padding: "2px 4px",
                          },
                      }
                    : {
                          pointerEvents: "none",
                      }),
            }}
            onPointerEnter={(e) => {
                if (hasMouse) {
                    e.currentTarget.classList.add("line-label-hovered");
                }
                onPointerEnter?.(e);
            }}
            onPointerLeave={(e) => {
                if (hasMouse) {
                    e.currentTarget.classList.remove("line-label-hovered");
                }
                onPointerLeave?.(e);
            }}
        >
            {onRemove && hasMouse && (
                <Box
                    className="laser-label-btn-container"
                    sx={{
                        position: "absolute",
                        left: "100%",
                        height: "100%",
                        backgroundColor: bgColor,
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        marginLeft: "-4px",
                        padding: "1px 2px",
                        paddingLeft: "5px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                    }}
                >
                    <IconButton
                        sx={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            background: "#F3BEBE",
                            color: "#C42223",
                            "&:hover": {
                                background: "#F3BEBE",
                            },
                        }}
                        onClick={onRemove}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <Box
                className="label-label-text"
                sx={{
                    borderRadius: "4px",
                    padding: "0px 4px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: bgColor,
                    color: textColor,
                    fontWeight,
                    fontSize,
                    whiteSpace: "nowrap",
                    lineHeight,
                    fontFamily,
                }}
            >
                {children}
            </Box>
            {touchRemove && (
                <IconButton
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        background: "#F3BEBE",
                        color: "#C42223",
                        // Same 2px gap as the coordinate labels, which get it from 6px less the
                        // text's -4px right margin.
                        marginLeft: "2px",
                        "&:hover": { background: "#F3BEBE" },
                    }}
                    onClick={onRemove}
                >
                    <Close sx={{ fontSize: 20 }} />
                </IconButton>
            )}
        </Box>
    );
}
